import React, { useState, useEffect } from 'react';
import { Layers, X, Save, Plus, Trash2, Swords, Trophy, Sparkles, Palette, ChevronDown } from 'lucide-react';
import { useCollection } from '../context/CollectionContext';
import { useLanguage } from '../context/LanguageContext';
import { Deck, DeckStrategyGuide } from '../types';
import { soundEffects } from '../services/audio';

interface EditDeckModalProps {
  deck: Deck | null;
  isOpen: boolean;
  onClose: () => void;
}

const COLOR_PRESETS = [
  { name: 'Red', color: '#EF4444', badge: 'from-red-600 to-red-950' },
  { name: 'Blue', color: '#3B82F6', badge: 'from-blue-600 to-blue-950' },
  { name: 'Purple', color: '#8B5CF6', badge: 'from-purple-600 to-purple-950' },
  { name: 'Emerald', color: '#10B981', badge: 'from-emerald-600 to-emerald-950' },
  { name: 'Amber', color: '#F59E0B', badge: 'from-amber-600 to-amber-950' },
  { name: 'Cyan', color: '#06B6D4', badge: 'from-cyan-600 to-cyan-950' },
];

export const EditDeckModal: React.FC<EditDeckModalProps> = ({ deck, isOpen, onClose }) => {
  const { updateDeck } = useCollection();
  const { t, localizePlaybookTitle, localizePlaybookStep, localizePrizeTradeTip, localizeDeckText } = useLanguage();

  const [activeTab, setActiveTab] = useState<'playbook' | 'general'>('playbook');
  const [name, setName] = useState('');
  const [format, setFormat] = useState<'Standard' | 'Expanded' | 'Casual'>('Standard');
  const [archetype, setArchetype] = useState('');
  const [accentColor, setAccentColor] = useState('#EF4444');
  const [badgeColor, setBadgeColor] = useState('from-red-600 to-red-950');
  const [summary, setSummary] = useState('');
  const [winCondition, setWinCondition] = useState('');
  const [prizeTradeTip, setPrizeTradeTip] = useState('');

  // Strategy Guide state
  const [openingTitle, setOpeningTitle] = useState(() => t('editDeck.defaultOpeningTitle'));
  const [openingSteps, setOpeningSteps] = useState<string[]>([]);

  const [midgameTitle, setMidgameTitle] = useState(() => t('editDeck.defaultMidgameTitle'));
  const [midgameSteps, setMidgameSteps] = useState<string[]>([]);

  const [lategameTitle, setLategameTitle] = useState(() => t('editDeck.defaultEndgameTitle'));
  const [lategameSteps, setLategameSteps] = useState<string[]>([]);

  // Sync state whenever active deck changes or modal opens
  useEffect(() => {
    if (deck) {
      setName(deck.name || '');
      setFormat((deck.format as any) || 'Standard');
      setArchetype(deck.archetype || '');
      setAccentColor(deck.accent_color || '#EF4444');
      setBadgeColor(deck.badge_color || 'from-red-600 to-red-950');
      setSummary(localizeDeckText(deck.summary || ''));
      setWinCondition(localizeDeckText(deck.win_condition || ''));
      setPrizeTradeTip(localizePrizeTradeTip(deck.prize_trade_tip || ''));

      const guide = deck.strategy_guide || {
        opening: { title: t('editDeck.defaultOpeningTitle'), steps: [] },
        midgame: { title: t('editDeck.defaultMidgameTitle'), steps: [] },
        lategame: { title: t('editDeck.defaultEndgameTitle'), steps: [] }
      };

      setOpeningTitle(localizePlaybookTitle(guide.opening?.title || t('editDeck.defaultOpeningTitle')));
      setOpeningSteps(guide.opening?.steps && guide.opening.steps.length > 0 ? guide.opening.steps.map(s => localizePlaybookStep(s)) : ['']);

      setMidgameTitle(localizePlaybookTitle(guide.midgame?.title || t('editDeck.defaultMidgameTitle')));
      setMidgameSteps(guide.midgame?.steps && guide.midgame.steps.length > 0 ? guide.midgame.steps.map(s => localizePlaybookStep(s)) : ['']);

      setLategameTitle(localizePlaybookTitle(guide.lategame?.title || t('editDeck.defaultEndgameTitle')));
      setLategameSteps(guide.lategame?.steps && guide.lategame.steps.length > 0 ? guide.lategame.steps.map(s => localizePlaybookStep(s)) : ['']);
    }
  }, [deck, isOpen]);

  if (!isOpen || !deck) return null;

  // Step Helpers
  const handleStepChange = (phase: 'opening' | 'midgame' | 'lategame', index: number, value: string) => {
    if (phase === 'opening') {
      const updated = [...openingSteps];
      updated[index] = value;
      setOpeningSteps(updated);
    } else if (phase === 'midgame') {
      const updated = [...midgameSteps];
      updated[index] = value;
      setMidgameSteps(updated);
    } else {
      const updated = [...lategameSteps];
      updated[index] = value;
      setLategameSteps(updated);
    }
  };

  const handleAddStep = (phase: 'opening' | 'midgame' | 'lategame') => {
    soundEffects.playClick();
    if (phase === 'opening') setOpeningSteps(prev => [...prev, '']);
    else if (phase === 'midgame') setMidgameSteps(prev => [...prev, '']);
    else setLategameSteps(prev => [...prev, '']);
  };

  const handleRemoveStep = (phase: 'opening' | 'midgame' | 'lategame', index: number) => {
    soundEffects.playClick();
    if (phase === 'opening') {
      setOpeningSteps(prev => prev.filter((_, i) => i !== index));
    } else if (phase === 'midgame') {
      setMidgameSteps(prev => prev.filter((_, i) => i !== index));
    } else {
      setLategameSteps(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    soundEffects.playScan();

    const cleanOpening = openingSteps.map(s => s.trim()).filter(Boolean);
    const cleanMidgame = midgameSteps.map(s => s.trim()).filter(Boolean);
    const cleanLategame = lategameSteps.map(s => s.trim()).filter(Boolean);

    const updatedGuide: DeckStrategyGuide = {
      opening: {
        title: openingTitle.trim() || t('editDeck.defaultOpeningTitle'),
        steps: cleanOpening.length > 0 ? cleanOpening : [t('editDeck.defaultOpeningTitle')]
      },
      midgame: {
        title: midgameTitle.trim() || t('editDeck.defaultMidgameTitle'),
        steps: cleanMidgame.length > 0 ? cleanMidgame : [t('editDeck.defaultMidgameTitle')]
      },
      lategame: {
        title: lategameTitle.trim() || t('editDeck.defaultEndgameTitle'),
        steps: cleanLategame.length > 0 ? cleanLategame : [t('editDeck.defaultEndgameTitle')]
      }
    };

    updateDeck(deck.id, {
      name: name.trim(),
      format,
      format_slug: format === 'Expanded' ? 'expanded' : (format === 'Casual' ? 'casual' : 'standard'),
      archetype: archetype.trim() || 'Custom Rogue',
      accent_color: accentColor,
      badge_color: badgeColor,
      summary: summary.trim() || deck.summary,
      win_condition: winCondition.trim() || deck.win_condition,
      prize_trade_tip: prizeTradeTip.trim() || deck.prize_trade_tip,
      strategy_guide: updatedGuide
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/85 backdrop-blur-md overflow-y-auto animate-in fade-in">
      <div className="relative w-full max-w-3xl bg-pokedex-screen border-4 border-pokedex-darkred rounded-3xl shadow-2xl overflow-hidden text-slate-100 my-8">
        {/* Top Header */}
        <div className="bg-pokedex-red px-6 py-3 border-b-2 border-pokedex-darkred flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Layers className="w-5 h-5 text-yellow-300" />
            <span className="font-display font-bold text-sm text-white uppercase tracking-wider">
              {t('editDeck.title')}: {deck.name}
            </span>
          </div>

          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="w-8 h-8 rounded-full bg-pokedex-darkred hover:bg-black/40 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-800 bg-pokedex-darker text-xs font-mono">
          <button
            type="button"
            onClick={() => { soundEffects.playClick(); setActiveTab('playbook'); }}
            className={`flex-1 py-3 font-bold flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'playbook' ? 'bg-slate-800 text-yellow-300 border-b-2 border-yellow-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Swords className="w-4 h-4" />
            <span>{t('editDeck.tabPlaybook')}</span>
          </button>
          <button
            type="button"
            onClick={() => { soundEffects.playClick(); setActiveTab('general'); }}
            className={`flex-1 py-3 font-bold flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'general' ? 'bg-slate-800 text-yellow-300 border-b-2 border-yellow-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Palette className="w-4 h-4" />
            <span>{t('editDeck.tabDetails')}</span>
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 max-h-[75vh] overflow-y-auto font-mono text-xs space-y-5">
          {activeTab === 'playbook' ? (
            <div className="space-y-6 animate-in fade-in">
              {/* Win Condition & Prize Trade Header Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-pokedex-darker p-3.5 rounded-2xl border border-slate-800 space-y-1.5">
                  <label className="text-yellow-400 flex items-center gap-1.5 font-bold uppercase text-[10px]">
                    <Trophy className="w-3.5 h-3.5" />
                    <span>{t('editDeck.winConditionLabel')}</span>
                  </label>
                  <textarea
                    rows={2}
                    value={winCondition}
                    onChange={(e) => setWinCondition(e.target.value)}
                    placeholder={t('editDeck.winConditionPlaceholder')}
                    className="w-full bg-black/60 border border-slate-700 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                  />
                </div>

                <div className="bg-pokedex-darker p-3.5 rounded-2xl border border-slate-800 space-y-1.5">
                  <label className="text-purple-300 flex items-center gap-1.5 font-bold uppercase text-[10px]">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{t('editDeck.prizeTradeLabel')}</span>
                  </label>
                  <textarea
                    rows={2}
                    value={prizeTradeTip}
                    onChange={(e) => setPrizeTradeTip(e.target.value)}
                    placeholder={t('editDeck.prizeTradePlaceholder')}
                    className="w-full bg-black/60 border border-slate-700 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                  />
                </div>
              </div>

              {/* 1. Opening / Early Game Section */}
              <div className="bg-pokedex-darker p-4 rounded-2xl border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                    <span className="font-bold text-sm text-blue-400 uppercase">{t('editDeck.openingPlan')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddStep('opening')}
                    className="text-xs text-yellow-300 hover:text-yellow-200 flex items-center gap-1 bg-black/40 px-2.5 py-1 rounded-lg border border-slate-700"
                  >
                    <Plus className="w-3 h-3" />
                    <span>{t('editDeck.addStep')}</span>
                  </button>
                </div>

                <input
                  type="text"
                  value={openingTitle}
                  onChange={(e) => setOpeningTitle(e.target.value)}
                  placeholder={t('editDeck.sectionTitlePlaceholder')}
                  className="w-full bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-yellow-300 focus:outline-none focus:border-pokedex-blue font-sans"
                />

                <div className="space-y-2">
                  {openingSteps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-slate-500 font-mono text-[10px] w-4 shrink-0">{idx + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => handleStepChange('opening', idx, e.target.value)}
                        placeholder={t('editDeck.stepPlaceholder', { num: idx + 1 })}
                        className="flex-1 bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveStep('opening', idx)}
                        className="text-slate-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-black/40 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. Midgame Section */}
              <div className="bg-pokedex-darker p-4 rounded-2xl border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span>
                    <span className="font-bold text-sm text-teal-400 uppercase">{t('editDeck.midgamePlan')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddStep('midgame')}
                    className="text-xs text-yellow-300 hover:text-yellow-200 flex items-center gap-1 bg-black/40 px-2.5 py-1 rounded-lg border border-slate-700"
                  >
                    <Plus className="w-3 h-3" />
                    <span>{t('editDeck.addStep')}</span>
                  </button>
                </div>

                <input
                  type="text"
                  value={midgameTitle}
                  onChange={(e) => setMidgameTitle(e.target.value)}
                  placeholder={t('editDeck.sectionTitlePlaceholder')}
                  className="w-full bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-yellow-300 focus:outline-none focus:border-pokedex-blue font-sans"
                />

                <div className="space-y-2">
                  {midgameSteps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-slate-500 font-mono text-[10px] w-4 shrink-0">{idx + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => handleStepChange('midgame', idx, e.target.value)}
                        placeholder={t('editDeck.stepPlaceholder', { num: idx + 1 })}
                        className="flex-1 bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveStep('midgame', idx)}
                        className="text-slate-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-black/40 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3. Endgame / Lategame Section */}
              <div className="bg-pokedex-darker p-4 rounded-2xl border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                    <span className="font-bold text-sm text-purple-400 uppercase">{t('editDeck.endgamePlan')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddStep('lategame')}
                    className="text-xs text-yellow-300 hover:text-yellow-200 flex items-center gap-1 bg-black/40 px-2.5 py-1 rounded-lg border border-slate-700"
                  >
                    <Plus className="w-3 h-3" />
                    <span>{t('editDeck.addStep')}</span>
                  </button>
                </div>

                <input
                  type="text"
                  value={lategameTitle}
                  onChange={(e) => setLategameTitle(e.target.value)}
                  placeholder={t('editDeck.sectionTitlePlaceholder')}
                  className="w-full bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-yellow-300 focus:outline-none focus:border-pokedex-blue font-sans"
                />

                <div className="space-y-2">
                  {lategameSteps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-slate-500 font-mono text-[10px] w-4 shrink-0">{idx + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => handleStepChange('lategame', idx, e.target.value)}
                        placeholder={t('editDeck.stepPlaceholder', { num: idx + 1 })}
                        className="flex-1 bg-black/60 border border-slate-700 rounded-xl p-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveStep('lategame', idx)}
                        className="text-slate-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-black/40 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('editDeck.deckName')}</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('editDeck.namePlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue font-sans text-xs"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('editDeck.archetype')}</label>
                  <input
                    type="text"
                    value={archetype}
                    onChange={(e) => setArchetype(e.target.value)}
                    placeholder={t('editDeck.archetypePlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue font-sans text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('editDeck.format')}</label>
                  <div className="relative">
                    <select
                      value={format}
                      onChange={(e) => setFormat(e.target.value as any)}
                      className="w-full appearance-none bg-pokedex-darker border border-slate-700 rounded-xl pl-3 pr-8 py-2.5 text-white focus:outline-none focus:border-pokedex-blue font-sans text-xs cursor-pointer"
                    >
                      <option value="Standard">{t('editDeck.formatStandard')}</option>
                      <option value="Expanded">{t('editDeck.formatExpanded')}</option>
                      <option value="Casual">{t('editDeck.formatCasual')}</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('editDeck.accentColor')}</label>
                  <div className="flex items-center gap-2 pt-1">
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => {
                          setAccentColor(preset.color);
                          setBadgeColor(preset.badge);
                        }}
                        style={{ backgroundColor: preset.color }}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          accentColor === preset.color ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('editDeck.summary')}</label>
                <textarea
                  rows={3}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder={t('editDeck.summaryPlaceholder')}
                  className="w-full bg-pokedex-darker border border-slate-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue font-sans text-xs"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors font-mono text-xs text-center"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto bg-pokedex-red hover:bg-pokedex-lightred text-white font-bold px-6 py-2.5 rounded-xl shadow-lg transition-all active:scale-95 text-xs font-mono uppercase tracking-wider flex items-center justify-center gap-2 border border-white/20"
            >
              <Save className="w-4 h-4 text-yellow-300" />
              <span>{t('editDeck.saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
